import React, { useEffect } from 'react';
import { push } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
	reduxForm,
	submit,
	updateSyncErrors,
	updateSyncWarnings,
	isAsyncValidating,
	destroy
} from 'redux-form';
import {
	Modal,
	ProgressIndicator,
	Button
} from 'rhombus-react';

import useActiveCreative from 'hooks/useActiveCreative';

import { clearDetails, loadCreative, saveSocial2Creative } from 'reducers/CreativeDetails';

import { GOOGLE_DSP_INTERNAL_NAME } from 'social/constants';
import { FORM_BY_ADTYPE } from 'social/constants/forms';
import { deserializeByAdType } from 'social/deserializers';
import { serializeByAdType } from 'social/serializers';
import { VALIDATORS_BY_ADTYPE, WARNINGS_BY_ADTYPE } from 'social/validators/validators';
import { normalizeCardAPIWarnings } from "social/utils";

import { useModalStack } from 'utils/common';
import { SOCIAL_PLATFORM_TYPES } from 'utils/constants';
import { isWizardValid } from 'selectors/Social2';
import { hasVersionsSelector } from "selectors/Creatives";

import InfoBanner from 'widgets/InfoBanner';
import { SOCIAL_WIZARD } from 'widgets/forms/list';

import "./SocialModal.css";
import "./EditSocialModal.css";

function canEdit(status) {
	return status !== "APPROVED" && status !== "SUBMITTED";
}

function normalizeAPIWarning(key, message) {
	/*
		The api returns asset errors as such:
			video.id: ["id is required"]

		This would not make much sense to a user, so this function looks to normalize those warnings
		into more user friendly messages. The above message would become "Video - Video is required".
	*/
	if (/^image\./gi.test(key)) {
		const [, replaceValue] = key.split(".");
		key = "image";
		message = message.replace(replaceValue, "Image");
	}

	if (/^video\./gi.test(key)) {
		const [, replaceValue] = key.split(".");
		key = "video";
		message = message.replace(replaceValue, "Video");
	}

	if (/^cta\./gi.test(key)) {
		const [, replaceValue] = key.split(".");
		key = "cta";
		message = message.replace(replaceValue, "CTA");
	}

	if (/^placements/gi.test(key)) {
		key = "_select-aspect-ratios";
	}

	return { normalKey: key, normalMessage: message };
}

export function apiWarningReducer(warnings, socialValues = {}) {
	return Object.keys(warnings).reduce((prev, key) => {
		const messages = warnings[key] || [];

		if(key === "cards"){ // These are warnings for Carousel cards
			return {
				...prev,
				...normalizeCardAPIWarnings(messages, socialValues.cards),
			};
		}

		messages.forEach((messageData = "") => {
			if (typeof messageData === "object") {
				/*
					In the case of placements, we could hit an object here. E.g.

					{
						...otherWarnings,
						placements: [
							{
								type: { id: 1, name: "Square" },
								image: ["Image is required"],
								headline: ["Headline is too long"]
							}
						],
					}

					When this happens, we use recursion to drill down and loop through those
					and attach the warnings matching UI schema of:

					{
						...otherWarnings,
						placements: {
							Square: {
								image: ["Image is required"],
								headline: ["Headline is too long"]
							}
						}
					}
				*/

				const { name } = messageData.type || {};
				if (!name) {
					/*
						If, somehow, we are not given a name, we have no meaningful way to
						interpret these warnings, so we ignore and continue to the next key
					*/
					return prev;
				}

				delete messageData.type; // We no longer need the type, since we already destructured the name above

				const messages = apiWarningReducer(messageData);
				prev[key] = {
					...prev[key],
					[name]: messages,
				};
				return prev;
			}

			const { normalKey, normalMessage } = normalizeAPIWarning(key, messageData);

			if (!prev[normalKey]) {
				prev[normalKey] = [];
			}

			if (normalMessage) {
				prev[normalKey].push(normalMessage);
			}
		});

		return prev;
	}, {});
}

const formName = SOCIAL_WIZARD;


function EditSocialModal(props) {
	const {
		libraryId,
		creativeId,
		returnPath,
		initialize,
		dirty,
		socialState = {}, /*
			This is given as a prop (rather than just being selected) because it's needed for
			serializing the Creative on submit, and the redux-form onSubmit function receives
			the decorated component's props as an argument, but otherwise has no way to access it.
		*/
	} = props;

	const dispatch = useDispatch();
	const creative = useSelector(state => state.creativeDetails);
	const isFormValid = useSelector(isWizardValid);
	const isValidating = useSelector(isAsyncValidating(SOCIAL_WIZARD));
	const doVersionsExist = useSelector(hasVersionsSelector);

	const { isLoaded: isDSPLoaded, items: dspConnections = []} = socialState || {};
	const hasGoogleConnection = dspConnections.find(connection => connection.internalName === GOOGLE_DSP_INTERNAL_NAME);

	const { name, adType, approval, isSaving } = creative;
	const isViewOnly = !canEdit(approval);

	const platformClass = SOCIAL_PLATFORM_TYPES[adType];
	const title = name ? `Edit Creative - ${name}` : "Edit Creative";

	let commonModalProps = {
		title,
		setAppTitle: false,
		type: "modal",
		className: `edit-social-modal social-modal ${platformClass}`,
		libraryId,
		creative,

		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
	};

	const [modalStack, navigationActions, ModalStackHead] = useModalStack(commonModalProps);

	useActiveCreative(creativeId);

	useEffect(() => {
		if(!isDSPLoaded || (hasGoogleConnection && !socialState.googleLocales.isLoaded)){ return; } /*
			Google locale data is needed to properly deserialize certain adTypes.
			It's loaded with the Creatives grid data, so it shouldn't be a large delay to just
			wait for it, in the worst case.  In the best case, it's already loaded before the modal opens.

			We could do this check selectively, but it would be a big complexity increase,
			given that we don't know the adType until the Creative has been loaded.
		*/
		dispatch(clearDetails());
		dispatch(loadCreative(creativeId)).then((resp) => {
			if (resp?.success) {
				const { data } = resp?.payload || {};
				const {
					adType = "",
					warnings = {},
					errors = {},
					socialValues = {},
				} = data;

				const initValues = deserializeByAdType(data, adType, socialState);
				dispatch(initialize(initValues));

				const hasWarning = Object.keys(warnings).length > 0;
				if (hasWarning) {
					const warningMessages = apiWarningReducer(warnings, socialValues);
					dispatch(updateSyncWarnings(formName, { [adType]: warningMessages }));
				}

				const hasError = Object.keys(errors).length > 0;
				if (hasError) {
					const errorMessages = apiWarningReducer(errors);
					dispatch(updateSyncErrors(formName, { [adType]: errorMessages }));
				}
			}
		});

		return () => dispatch(clearDetails());
	}, [isDSPLoaded, hasGoogleConnection, socialState.googleLocales.isLoaded]);

	function onCreateConcept(){
		dispatch(submit(formName));
	}

	const FormForAdType = FORM_BY_ADTYPE[adType];

	function closeModal() {
		if (modalStack.length) {
			navigationActions.popModal();
			return;
		}

		let shouldClose = true;

		if (!isViewOnly && dirty) {
			shouldClose = confirm("Are you sure you want to cancel the creative edit? All data entered will be lost.");
		}

		if (!shouldClose) { return; }

		dispatch(push(returnPath));
		dispatch(destroy(SOCIAL_WIZARD));
	}

	commonModalProps.onClose = closeModal;

	let form;
	if (FormForAdType) {
		form = (
			<FormForAdType
				navigationActions={navigationActions}
				libraryId={libraryId}
				isViewOnly={isViewOnly}
				doVersionsExist={doVersionsExist}
				showPristineErrors
				isEditMode
			/>
		);
	}

	let infoBanner;
	if (isViewOnly) {
		infoBanner = (
			<InfoBanner>
				Please Note: Editing is disabled when the creative status is Approved or In QA.
			</InfoBanner>
		);
	}

	const isLoading = !FormForAdType || isSaving;

	function renderContent() {
		return (
			<div className = "main-content-container">
				<ProgressIndicator isLoading={isLoading} />
				<div className="scroll-container">
					{infoBanner}
					{form}
				</div>
			</div>
		);
	}

	function renderFooter() {
		return (
			<>
				<Button theme="default" onClick={closeModal}>Cancel</Button>
				{!isViewOnly && (<div className="right-buttons">
					<Button
						theme="green"
						type="submit"
						onClick={onCreateConcept}
						disabled={isLoading || !isFormValid || isValidating}
					>
						Save Updates
					</Button>
				</div>)}
			</>
		);
	}

	let modalProps = {
		...commonModalProps,
		renderContent,
		renderFooter,
	};

	if (modalStack.length) {
		return <ModalStackHead commonModalProps={commonModalProps} />;
	}

	return <Modal {...modalProps} />;
}

function handleWarn(values, props) {
	const { adType } = props.values || {};
	const warn = WARNINGS_BY_ADTYPE[adType];

	if (warn) {
		return warn(values, props);
	}
}

function handleValidate(values, props) {
	const { adType } = props.values || {};
	const validator = VALIDATORS_BY_ADTYPE[adType];
	if (validator) {
		return validator(values, props);
	}
}

function handleSubmit(values, dispatch, formProps) {
	const { returnPath, socialState } = formProps;
	const { adType } = values;
	const data = serializeByAdType(values, adType, socialState);

	dispatch(saveSocial2Creative(data))
		.then((resp) => {
			if(resp?.success){
				dispatch(push(returnPath));
			}
		});
}

export default reduxForm({
	form: formName,

	shouldWarn(params){
		const { deepEqual } = params.structure;
		const { adType } = params.props.values;

		const currentActivePAC = params.props.values[adType]?._currentActivePAC;
		const nextActivePAC = params.nextProps?.values?.[adType]?._currentActivePAC;

		if(currentActivePAC !== nextActivePAC){
			/*
				Normally, we want to revalidate when changing the active PAC, in order to
				set the validation status color on the PAC button, but when editing
				we don't want to clobber the errors we got from the API.

				When we're creating, setting the validation color is more relevant,
				because you may not have touched the placement form yet, and we leave it neutral
				until you have, but after saving, the buttons are all treated as touched already,
				and will be red or not based on the API validation.
			*/
			return false;
		}

		return !deepEqual(params.nextProps?.values, params.props.values);
	},

	warn: handleWarn,
	validate: handleValidate,
	onSubmit: handleSubmit,

	destroyOnUnmount: false, // Preserve errors
})(EditSocialModal);
